/**
 * Video Generation Store
 * Manages video generation state with Zustand
 * Validates: Requirements 17.1, 17.2, 17.3, 17.6
 */
import { create } from 'zustand';
import apiClient from '../api/client';

/**
 * Audio timing segment for Veo 3
 * Validates: Requirements 17.2
 */
export interface AudioTimingSegment {
  startTime: number;
  endTime: number;
  text: string;
  voiceStyle?: string;
}

/**
 * Video generation settings
 */
export interface VideoGenerationSettings {
  aspectRatio?: '16:9' | '9:16' | '1:1' | '4:3' | '3:4';
  duration?: number;
  fps?: number;
  resolution?: '720p' | '1080p' | '4k';
  audioTiming?: AudioTimingSegment[];
}

/**
 * Video result from generation
 */
export interface VideoResult {
  url: string;
  thumbnailUrl?: string;
  duration: number;
  resolution: string;
}

/**
 * Video generation job
 */
export interface VideoJob {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  provider: string;
  model: string;
  prompt: string;
  enhancedPrompt?: string;
  settings: VideoGenerationSettings;
  video?: VideoResult;
  progress?: number;
  error?: string;
  createdAt: Date;
  completedAt?: Date | null;
}

/**
 * Video model info
 */
export interface VideoModelInfo {
  id: string;
  name: string;
  scriptLimit: number;
  durationLimits: { min: number; max: number };
  features: string[];
}

/**
 * Video provider info
 */
export interface VideoProviderInfo {
  id: string;
  name: string;
  models: VideoModelInfo[];
}

interface VideoState {
  // Jobs
  jobs: VideoJob[];
  currentJob: VideoJob | null;
  jobsLoading: boolean;
  
  // Providers and models
  providers: VideoProviderInfo[];
  providersLoading: boolean;
  selectedProvider: string;
  selectedModel: string;
  
  // Generation state
  isGenerating: boolean;
  error: string | null;
  
  // Settings
  promptEnhancerEnabled: boolean;
  settings: VideoGenerationSettings;
  audioTimingJson: string;
  
  // Actions
  loadProviders: () => Promise<void>;
  loadJobs: () => Promise<void>;
  loadJob: (jobId: string) => Promise<void>;
  generateVideo: (prompt: string) => Promise<void>;
  setSelectedProvider: (provider: string) => void;
  setSelectedModel: (model: string) => void;
  setSettings: (settings: Partial<VideoGenerationSettings>) => void;
  setAudioTimingJson: (json: string) => void;
  togglePromptEnhancer: () => void;
  clearError: () => void;
  pollJobStatus: (jobId: string) => Promise<void>;
}

export const useVideoStore = create<VideoState>((set, get) => ({
  // Initial state
  jobs: [],
  currentJob: null,
  jobsLoading: false,
  providers: [],
  providersLoading: false,
  selectedProvider: 'google',
  selectedModel: 'veo-3',
  isGenerating: false,
  error: null,
  promptEnhancerEnabled: false,
  settings: {
    aspectRatio: '16:9',
    duration: 10,
    fps: 30,
    resolution: '1080p',
  },
  audioTimingJson: '',

  /**
   * Load available providers and models
   */
  loadProviders: async () => {
    set({ providersLoading: true });
    try {
      const response = await apiClient.get('/api/videos/providers');
      const providers = response.data.providers as VideoProviderInfo[];
      set({ providers, providersLoading: false });
      
      // Set default selection if not already set
      if (providers.length > 0 && providers[0]!.models.length > 0) {
        const state = get();
        if (!state.selectedProvider || !state.selectedModel) {
          set({
            selectedProvider: providers[0]!.id,
            selectedModel: providers[0]!.models[0]!.id,
          });
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load providers';
      set({ error: message, providersLoading: false });
    }
  },

  /**
   * Load video generation jobs
   * Validates: Requirements 17.6
   */
  loadJobs: async () => {
    set({ jobsLoading: true });
    try {
      const response = await apiClient.get('/api/videos');
      const jobs = (response.data.jobs as VideoJob[]).map(job => ({
        ...job,
        createdAt: new Date(job.createdAt),
        completedAt: job.completedAt ? new Date(job.completedAt) : null,
      }));
      set({ jobs, jobsLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load jobs';
      set({ error: message, jobsLoading: false });
    }
  },

  /**
   * Load a specific job
   */
  loadJob: async (jobId: string) => {
    try {
      const response = await apiClient.get(`/api/videos/${jobId}`);
      const job = {
        ...response.data.job,
        createdAt: new Date(response.data.job.createdAt),
        completedAt: response.data.job.completedAt 
          ? new Date(response.data.job.completedAt) 
          : null,
      } as VideoJob;
      set({ currentJob: job });
      
      // Update in jobs list
      const jobs = get().jobs.map(j => j.id === jobId ? job : j);
      set({ jobs });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load job';
      set({ error: message });
    }
  },

  /**
   * Generate a new video
   * Validates: Requirements 17.2, 17.3, 17.4
   */
  generateVideo: async (prompt: string) => {
    const state = get();
    set({ isGenerating: true, error: null });

    try {
      // Parse audio timing JSON if provided
      let audioTiming: AudioTimingSegment[] | undefined;
      if (state.audioTimingJson.trim()) {
        try {
          audioTiming = JSON.parse(state.audioTimingJson);
        } catch {
          throw new Error('Invalid audio timing JSON format');
        }
      }

      // Build settings with audio timing
      const settings: VideoGenerationSettings = {
        ...state.settings,
        audioTiming,
      };

      // Enhance prompt if enabled
      let enhancedPrompt: string | undefined;
      if (state.promptEnhancerEnabled) {
        try {
          const enhanceResponse = await apiClient.post('/api/enhance', {
            prompt,
            targetType: 'video',
            model: state.selectedModel,
          });
          enhancedPrompt = enhanceResponse.data.enhancedPrompt;
        } catch {
          // Continue without enhancement if it fails
          console.warn('Prompt enhancement failed, using original prompt');
        }
      }

      // Create video generation job
      const response = await apiClient.post('/api/videos/generate', {
        prompt,
        provider: state.selectedProvider,
        model: state.selectedModel,
        settings,
        enhancedPrompt,
      });

      const job = {
        ...response.data.job,
        createdAt: new Date(response.data.job.createdAt),
        completedAt: response.data.job.completedAt 
          ? new Date(response.data.job.completedAt) 
          : null,
      } as VideoJob;

      // Add to jobs list
      set({ 
        jobs: [job, ...state.jobs],
        currentJob: job,
        isGenerating: false,
      });

      // Start polling for status
      get().pollJobStatus(job.id);
    } catch (error: unknown) {
      let message = 'Failed to generate video';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        message = axiosError.response?.data?.message || message;
      } else if (error instanceof Error) {
        message = error.message;
      }
      set({ error: message, isGenerating: false });
    }
  },

  /**
   * Poll job status until completion
   */
  pollJobStatus: async (jobId: string) => {
    const pollInterval = 3000; // 3 seconds
    const maxAttempts = 200; // ~10 minutes

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));

      try {
        const response = await apiClient.get(`/api/videos/${jobId}`);
        const job = {
          ...response.data.job,
          createdAt: new Date(response.data.job.createdAt),
          completedAt: response.data.job.completedAt 
            ? new Date(response.data.job.completedAt) 
            : null,
        } as VideoJob;

        // Update current job if it matches
        const state = get();
        if (state.currentJob?.id === jobId) {
          set({ currentJob: job });
        }

        // Update in jobs list
        const jobs = state.jobs.map(j => j.id === jobId ? job : j);
        set({ jobs });

        // Stop polling if job is complete or failed
        if (job.status === 'completed' || job.status === 'failed') {
          break;
        }
      } catch {
        // Continue polling on error
      }
    }
  },

  setSelectedProvider: (provider: string) => {
    const state = get();
    const providerInfo = state.providers.find(p => p.id === provider);
    const firstModel = providerInfo?.models[0]?.id || '';
    set({ selectedProvider: provider, selectedModel: firstModel });
  },

  setSelectedModel: (model: string) => {
    set({ selectedModel: model });
  },

  setSettings: (settings: Partial<VideoGenerationSettings>) => {
    set({ settings: { ...get().settings, ...settings } });
  },

  setAudioTimingJson: (json: string) => {
    set({ audioTimingJson: json });
  },

  togglePromptEnhancer: () => {
    set({ promptEnhancerEnabled: !get().promptEnhancerEnabled });
  },

  clearError: () => {
    set({ error: null });
  },
}));
